import {PracticeType} from "../../../../../models/Practice/Practice";
import {InsuranceProviderAuthorizationHttpService} from "../../../../../services/Http/InsuranceProviderAuthorizationHttpService";
import {InsuranceProviderAuthorizationOperationType} from "../../../../../models/InsuranceProviderAuthorization";
import {
    Alert,
    Button,
    Col,
    Form, FormInstance,
    FormListFieldData,
    InputNumber,
    Modal,
    Row,
    Typography
} from "antd";
import {AddValue} from "../AddValue/AddValues";
import {AddPerformance} from "../AddPerformance/AddPerformance";
import React, {ReactElement, useEffect, useState} from "react";
import {PracticesHttpService} from "../../../../../services/Http/PracticesHttpService";
import {useParams} from "react-router-dom";
import {DeleteOutlined} from "@ant-design/icons"
import {OrderData, PracticeDetail, UploadEstimateBodyRequest} from "../../../../../models/Practice/PracticeDetail";
import {SelectionValue} from "../../../../../models/Common/SelectionValue";
import {isNil} from "lodash";

export interface PreventiveDialogProps{
    practiceDetail?: PracticeDetail;
    isModalOpen:boolean,
    onSubmit: (body: UploadEstimateBodyRequest) => Promise<void>;
    onCancel:() => void,
    agreement:PracticeType,
    integrationCode:string
    insuranceProviderId:number,
    locationCode: string;
}

const {Text} = Typography;

interface OrderForm {
    service: string;
    amount: number;
    amountRaw?: number;
}
interface TotalAmount {
    totalAmount: number;
    totalAmountRaw: number;
}

interface EstimateForm {
    prezzoListino?:{
        montatura?: number;
        lenti?: number;
        altro?: number;
    },
    prezzoScontato?:{
        montatura?: number;
        lenti?: number;
        altro?: number;
    },
    orders: Array<OrderForm>;
}

/*
    I computer fanno cacare a gestire i float point!
    Se non ci credi prova a fare: (0.1 + 0.2) == 0.3
 */
function floatEquals(num1: number, num2: number) {
    const tollerance = 0.00000001;

    return Math.abs(num1 - num2) <= tollerance;
}

export function PreventiveDialog(props: PreventiveDialogProps) {
    const { id } = useParams();
    const [form] = Form.useForm();

    const [services,setServices]   = useState<Array<SelectionValue>>([]);
    const [isSubmitting, setSubmitting] = useState(false);
    const [insuranceSettings, setInsuranceSettings] = useState<string[]>([]);

    const CONSTANT_PREZZO_LISTINO = 'prezzoListino';
    const CONSTANT_PREZZO_SCONTATO = 'prezzoScontato';

    useEffect(() => {
        loadServices();
    }, []);

    useEffect(() => {
        if (props.insuranceProviderId) {
            InsuranceProviderAuthorizationHttpService.getByInsuranceProviderId(props.insuranceProviderId)
                .then(res => setInsuranceSettings(res.data))
                .catch(() => setInsuranceSettings([]));
        }
    }, [props.insuranceProviderId]);

    const loadServices = async () => {
        let data;
        if (props.agreement === PracticeType.INDIRECT) {
            data = [
                {
                    value: "MTT",
                    label: "Montatura"
                },
                {
                    value: "4162",
                    label: "Lenti"
                },
                {
                    value: "ALT",
                    label: "Altro (accessori, …)"
                }
            ];
        } else if (isNil(id)) {
            const response = await PracticesHttpService.getServicesByGenericInfo(props.insuranceProviderId, props.locationCode);
            data = response.data;
        } else {
            const response = await PracticesHttpService.getServicesByClaimId(id.toString());
            data = response.data;
        }
        setServices(data);
    }

    const insertRawAmount = insuranceSettings.includes(InsuranceProviderAuthorizationOperationType.INSERT_RAW_AMOUNT);
    const requirePrezzoScontatoSplit = insuranceSettings.includes(InsuranceProviderAuthorizationOperationType.REQUIRE_PREZZO_SCONTATO_SPLIT);
    const requirePrezzoListinoSplit = insuranceSettings.includes(InsuranceProviderAuthorizationOperationType.REQUIRE_PREZZO_LISTINO_SPLIT);

    const onCancel = () => {
        form.resetFields();
        props.onCancel();
    }

    const onFinish = async (data: EstimateForm) => {
        let note = "";
        if (requirePrezzoListinoSplit) {
            const totals = calculateTotal(form);
            const totalSplit = calculateSplitTotal(form, CONSTANT_PREZZO_LISTINO);

            if (!floatEquals(totals.totalAmountRaw, totalSplit)) {
                return;
            }

            note += "Prezzo listino: ";
            note += [
                data.prezzoListino?.montatura ? `Montatura: ${data.prezzoListino.montatura}` : null,
                data.prezzoListino?.lenti ? `Lenti: ${data.prezzoListino.lenti}` : null,
                data.prezzoListino?.altro ? `Altro: ${data.prezzoListino.altro}` : 'Altro: 0'
            ].filter(Boolean).join(', ') + ", \n";
        }

        if (requirePrezzoScontatoSplit) {
            const totals = calculateTotal(form);
            const totalSplit = calculateSplitTotal(form, CONSTANT_PREZZO_SCONTATO);

            if (!floatEquals(totals.totalAmount, totalSplit)) {
                return;
            }

            note +=  requirePrezzoListinoSplit ? "Prezzo scontato: " : "";
            note += [
                data.prezzoScontato?.montatura ? `Montatura: ${data.prezzoScontato.montatura}` : null,
                data.prezzoScontato?.lenti ? `Lenti: ${data.prezzoScontato.lenti}` : null,
                data.prezzoScontato?.altro ? `Altro: ${data.prezzoScontato.altro}` : 'Altro: 0'
            ].filter(Boolean).join(', ');
        }

        const ordersToSend: Array<OrderData> = data.orders.map(o => {
            let service = services!.find(s => s.value == o.service);

            return {
                code: o.service,
                label: service!.label ?? "",
                quantity: 1,
                amount: o.amount,
                amountRaw: o.amountRaw
            }
        })

        const body: UploadEstimateBodyRequest  = {
            orders: ordersToSend,
            note
        }

        setSubmitting(true)
        try {
            await props.onSubmit(body);
            form.resetFields();
        } finally {
            setSubmitting(false);
        }
    }

    const modalRender = (dom: any) => {
        return <Form layout="vertical"
                     form={form}
                     name="preventiveDialog"
                     onFinish={onFinish}
                     initialValues={{ orders: [{}] }}
        >{dom}</Form>
    }

    const calculateTotal = (formInstance: FormInstance<EstimateForm>):TotalAmount => {
        const orders: Array<OrderForm> = formInstance.getFieldValue('orders');
        return {
            totalAmount : orders.reduce((acc: number, o: OrderForm) => acc + (isNaN(o.amount) ? 0 : o.amount), 0),
            totalAmountRaw: orders.reduce((acc: number, o: OrderForm) => acc + (o?.amountRaw || 0), 0)
        }
    }

    const calculateSplitTotal = (formInstance: FormInstance<EstimateForm>, prezzoType:string) => {
        const values = form.getFieldsValue()[prezzoType];

        const montatura: number = values?.montatura ?? 0;
        const lenti: number = values?.lenti ?? 0;
        const altro: number = values?.altro ?? 0;

        return montatura + lenti + altro
    }

    /*
        Scusate, ma non so se esiste un modo migliore (non volendo definire un altro componente)
     */
    const renderRow = (key: string,
                       firstCol: ReactElement<any, any>,
                       secondCol: ReactElement<any, any>,
                       thirdCol: ReactElement<any, any>,
                       fourthCol: ReactElement<any, any>) => {
        return  <Row key={key} gutter={16}>
            <Col span={12}>
                {firstCol}
            </Col>
            { insertRawAmount && <Col span={5}>
                {secondCol}
            </Col> }
            <Col span={5}>
                {thirdCol}
            </Col>
            <Col span={2}>
                {fourthCol}
            </Col>
        </Row>
    }

    const renderOrder = (field: FormListFieldData, remove: (index: (number | number[])) => void) => {
        return renderRow(
            `order-${field.key}`,
            <AddPerformance performanceOptions={services} name={[field.name, 'service']}/>,
            <AddValue name={[field.name, 'amountRaw']}
                      otherValueName={[field.name, 'amount']}
                      compare={(value, otherValue) => value > otherValue}
                      errorMessage="Il prezzo di listino deve essere maggiore del prezzo scontato!"/>,
            <AddValue name={[field.name, 'amount']}
                      otherValueName={ insertRawAmount ? [field.name, 'amountRaw'] : null}
                      compare={(value, otherValue) => value < otherValue}
                      errorMessage="Il prezzo scontato deve essere minore del prezzo di listino!"/>,
            <Button onClick={() => remove(field.name)} danger>
                <DeleteOutlined />
            </Button>
        )
    }

    const renderPrezzoListinoSplit = () => {
        if (!requirePrezzoListinoSplit)
            return <></>

        const prezzoType = CONSTANT_PREZZO_LISTINO;

        return <>
            <hr style={{borderTop: 'grey'}}/>
            <Text strong>
                Suddivisione prezzo a listino
            </Text>
            <Row style={{marginTop: '10px'}} gutter={16}>
                <Col span={10}>
                    <Text>Valore Montatura Preventivo</Text>
                </Col>
                <Col span={14}>
                    <Form.Item name={[prezzoType, 'montatura']}>
                        <InputNumber min={0}
                                     addonAfter="€"
                                     className={"add-value-input-box"}
                                     decimalSeparator={","}
                                     controls={false} style={{width: '100%'}}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={10}>
                    <Text>Valore Lenti Preventivo</Text>
                </Col>
                <Col span={14}>
                    <Form.Item name={[prezzoType, 'lenti']} style={{flex: 1}}>
                        <InputNumber min={0}
                                     addonAfter="€"
                                     className={"add-value-input-box"}
                                     decimalSeparator={","}
                                     controls={false} style={{width: '100%'}}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={10}>
                    <Text>Valore Altro Preventivo</Text>
                </Col>
                <Col span={14}>
                    <Form.Item name={[prezzoType, 'altro']}>
                        <InputNumber min={0}
                                     addonAfter="€"
                                     className={"add-value-input-box"}
                                     decimalSeparator={","}
                                     controls={false} style={{width: '100%'}}/>
                    </Form.Item>
                </Col>
            </Row>


            <Form.Item<EstimateForm> shouldUpdate>
                {(formInstance) => {
                    const splitTotal = calculateSplitTotal(formInstance,prezzoType);
                    const totals = calculateTotal(formInstance);

                    return <>
                        <Row gutter={16}>
                            <Col span={10}>
                                <Text>Totale Valore Preventivo</Text>
                            </Col>
                            <Col span={14}>
                                {splitTotal}
                            </Col>
                        </Row>
                        {!floatEquals(totals.totalAmountRaw, splitTotal) && <Alert type="error"
                                                                   message="Il totale del preventivo deve essere uguale al totale splittato"
                                                                   style={{marginTop: '10px'}}/>}
                    </>
                }}
            </Form.Item>
        </>
    }

    const renderPrezzoScontatoSplit = () => {
        if (!requirePrezzoScontatoSplit)
            return <></>

        const prezzoType = CONSTANT_PREZZO_SCONTATO;

        return <>
            <hr style={{borderTop: 'grey'}}/>
            <Text strong>Suddivisione prezzo scontato</Text>
            <Row style={{marginTop: '10px'}} gutter={16}>
                <Col span={10}>
                    <Text>Valore Montatura Preventivo</Text>
                </Col>
                <Col span={14}>
                    <Form.Item name={[prezzoType, 'montatura']}>
                        <InputNumber min={0}
                                     addonAfter="€"
                                     className={"add-value-input-box"}
                                     decimalSeparator={","}
                                     controls={false} style={{width: '100%'}}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={10}>
                    <Text>Valore Lenti Preventivo</Text>
                </Col>
                <Col span={14}>
                    <Form.Item name={[prezzoType, 'lenti']} style={{flex: 1}}>
                        <InputNumber min={0}
                                     addonAfter="€"
                                     className={"add-value-input-box"}
                                     decimalSeparator={","}
                                     controls={false} style={{width: '100%'}}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={10}>
                    <Text>Valore Altro Preventivo</Text>
                </Col>
                <Col span={14}>
                    <Form.Item name={[prezzoType, 'altro']}>
                        <InputNumber min={0}
                                     addonAfter="€"
                                     className={"add-value-input-box"}
                                     decimalSeparator={","}
                                     controls={false} style={{width: '100%'}}/>
                    </Form.Item>
                </Col>
            </Row>


            <Form.Item<EstimateForm> shouldUpdate>
                {(formInstance) => {
                    const splitTotal = calculateSplitTotal(formInstance,prezzoType);
                    const totals = calculateTotal(formInstance);

                    return <>
                        <Row gutter={16}>
                            <Col span={10}>
                                <Text>Totale Valore Preventivo</Text>
                            </Col>
                            <Col span={14}>
                                {splitTotal}
                            </Col>
                        </Row>
                        {!floatEquals(totals.totalAmount, splitTotal) && <Alert type="error"
                                                                   message="Il totale del preventivo deve essere uguale al totale splittato"
                                                                   style={{marginTop: '10px'}}/>}
                    </>
                }}
            </Form.Item>
        </>
    }

    return <>
        <Modal width={820}
               closable
               open={props.isModalOpen}
               title="Preventivo"
               okButtonProps={{ loading: isSubmitting, autoFocus: true, htmlType: 'submit' }}
               destroyOnClose
               onCancel={onCancel}
               modalRender={modalRender}
               footer={[
                   <Button key="cancel" onClick={onCancel}>Annulla</Button>,
                   <Button key="ok" type="primary" loading={isSubmitting} onClick={() => form.submit()}>Ok</Button>,
                   props.integrationCode.includes('GENERALI') && <div key="footer-text" style={{ marginTop: '10px', fontSize: '14px', textAlign: 'left' }}>
                       <strong>Attenzione</strong>: ricordiamo che la Garanzia è sempre oggetto di rimborso da parte di Generali,
                       pertanto il suo eventuale importo dovrà sempre essere inserito all’interno del preventivo totale
                       aggiungendolo direttamente al totale <strong>Lenti</strong> o <strong>Lenti + Montatura</strong>
                   </div>
               ]}

        >
            {
                renderRow(
                    "header",
                    <Text strong>Prestazioni</Text>,
                    <Text strong>Prezzo listino</Text>,
                    <Text strong>Prezzo scontato</Text>,
                    <Text strong>Azioni</Text>
                )
            }
            <Form.List name="orders"
                       rules={[
                           {
                               validator: async (_, orders) => {
                                   if (!orders || orders.length < 1) {
                                       return Promise.reject(new Error('Devi inserire almeno una prestazione!'));
                                   }
                               }
                           }
                       ]}>
                    {(fields, {add, remove}, {errors}) => (
                    <div style={{marginTop: "10px"}}>
                        { fields.map(field => renderOrder(field, remove)) }
                        {!props.integrationCode.includes('GENERALI') && <Button block type="dashed" onClick={add} style={{marginBottom: '24px'}}>Aggiungi prestazioni</Button>}
                        <Form.ErrorList errors={errors} />
                    </div>
                )}
            </Form.List>
            <Form.Item<EstimateForm> shouldUpdate>
                {(formInstance) => {
                    const orders: Array<OrderForm> = formInstance.getFieldValue('orders');

                    const totalAmounts = calculateTotal(formInstance);

                    return renderRow(
                        "totals",
                        <Text strong>Totale preventivo</Text>,
                        <Text strong>{totalAmounts.totalAmountRaw.toFixed(2)}</Text>,
                        <Text strong>{totalAmounts.totalAmount.toFixed(2)}</Text>,
                        <></>
                    )
                }}
            </Form.Item>
            {renderPrezzoListinoSplit()}
            {renderPrezzoScontatoSplit()}
        </Modal>
    </>
}
